import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import TablesPage from "@/pages/TablesPage";
import LoginPage from "@/pages/LoginPage";
import UnauthorizedPage from "@/pages/UnauthorizedPage";

// Admin-only pages are code-split so the initial load (Login, Tables, Cafe for
// managers) stays lean — the heavy Recharts bundle only loads when an admin
// opens the Dashboard.
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CafePage = lazy(() => import("@/pages/CafePage"));
const StockPage = lazy(() => import("@/pages/StockPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const DuesPage = lazy(() => import("@/pages/DuesPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-64" role="status" aria-label="Loading">
      <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            <Route element={<ProtectedRoute allowedRoles={["Admin", "Manager"]} />}>
              <Route element={<Layout>
                <Suspense fallback={<PageFallback />}>
                  <Outlet />
                </Suspense>
              </Layout>}>
                <Route index element={<TablesPage />} />
                <Route path="/cafe" element={<CafePage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/dues" element={<DuesPage />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute allowedRoles={["Admin"]} />}>
              <Route element={<Layout>
                <Suspense fallback={<PageFallback />}>
                  <Outlet />
                </Suspense>
              </Layout>}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/stock" element={<StockPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>

          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#18181b",
                border: "1px solid #27272a",
                color: "#f4f4f5",
              },
            }}
          />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
